import {
  ActivityData,
  EngagementItem,
  EngagementRecord,
  EngagementType,
  ExposureBarItem,
  ExposureResponseItem,
  FormattedExposureData,
  PortfolioExposureData,
} from "../../models/stewardshipModels";
import { ROUTES } from "../../routes/routeNames";
import { Filter } from "../../types/PageFilterTypes";
import {
  formatReadable,
  formatReadableDate,
  getQuarterAndYearFromExcelDate,
  sortQuarters,
} from "../../utils/formattingUtils";

import {
  MILESTONE_DESCRIPTIONS,
  MILESTONES_START_COLUMN_MAP,
} from "./constants";
import { resolveEngagementMilestone } from "../../utils/engagementMilestones";

export const calculateTotalEngagementsByMilestone = (
  data: EngagementItem[],
  milestone: string | null,
  engagementType: string,
): number => {
  return data.filter(
    (item) =>
      (milestone === null || item.milestone_id === milestone) &&
      item.engagement_type_name === engagementType,
  ).length;
};

export const convertToEngagementItems = (
  inputs: EngagementRecord[],
): EngagementItem[] => {
  return inputs.map((input) => {
    const milestoneId = resolveEngagementMilestone(input);
    let lastActionDate = input.engagement_start;

    const milestoneStartColumn = MILESTONES_START_COLUMN_MAP[milestoneId];
    const milestoneStartDate = input[milestoneStartColumn] as string;

    if (
      milestoneStartDate &&
      milestoneStartDate !== "-" &&
      milestoneStartDate
    ) {
      lastActionDate = milestoneStartDate;
    }

    const engagementItem: EngagementItem = {
      milestone_id: milestoneId,
      milestone_description: MILESTONE_DESCRIPTIONS[milestoneId],
      theme: formatReadable(input.theme),
      sub_theme: formatReadable(input.sub_theme),
      manager_id: input.manager_id,
      manager_name: input.manager_name,
      manager_image: input.image || undefined,
      engagement_type_id: input.engagement_type,
      engagement_type_name: input.meta_theme,
      engagement_id: input.engagement_record_id,
      engagement_title: {
        displayValue: input.engagement_name,
        link: `${ROUTES.SINGLE_ENGAGEMENT}/${input.engagement_record_id}`,
      },
      last_action_date: formatReadableDate(lastActionDate),
      flagStatus: input.flag_status,
      company_id: input.company || undefined,
      company_name: input.company_name
        ? formatReadable(input.company_name)
        : undefined,
      company_image: input.company_image || undefined,
    };

    return engagementItem;
  });
};

export const transformEngagementData = (
  data: EngagementItem[],
): ActivityData[] => {
  const managersMap: { [key: string]: ActivityData } = {};

  data.forEach((item) => {
    // Initialize manager if not already in the map
    if (!managersMap[item.manager_name]) {
      managersMap[item.manager_name] = {
        manager: item.manager_name,
        [EngagementType.StrategyRiskCommunication]: 0,
        [EngagementType.Governance]: 0,
        [EngagementType.Social]: 0,
        [EngagementType.Environmental]: 0,
      };
    }

    // Increment the count based on engagement type
    switch (item.engagement_type_name) {
      case EngagementType.StrategyRiskCommunication:
        managersMap[item.manager_name][
          EngagementType.StrategyRiskCommunication
        ] += 1;
        break;
      case EngagementType.Governance:
        managersMap[item.manager_name][EngagementType.Governance] += 1;
        break;
      case EngagementType.Social:
        managersMap[item.manager_name][EngagementType.Social] += 1;
        break;
      case EngagementType.Environmental:
        managersMap[item.manager_name][EngagementType.Environmental] += 1;
        break;
    }
  });

  return Object.values(managersMap);
};

export const formatExposureData = (
  data: ExposureResponseItem[],
): FormattedExposureData[] => {
  if (!data || data.length === 0) return [];

  const dataWithQuarter = data.map((item) => ({
    ...item,
    quarter: getQuarterAndYearFromExcelDate(item.holding_date),
  }));

  const allQuarters = Array.from(
    new Set(dataWithQuarter.map((item) => item.quarter).filter(Boolean)),
  ).sort();

  const groupedData = new Map<string, Map<string, PortfolioExposureData>>();

  dataWithQuarter.forEach(
    ({ portfolio_index_id, quarter, voh, holding_weight }) => {
      if (!quarter) return;

      if (!groupedData.has(portfolio_index_id)) {
        groupedData.set(portfolio_index_id, new Map());
      }

      const quarterMap = groupedData.get(portfolio_index_id);
      if (quarterMap) {
        quarterMap.set(quarter, {
          quarter,
          date: quarter,
          exposure: voh,
          weight: holding_weight,
        });
      }
    },
  );

  groupedData.forEach((quarterMap) => {
    let lastKnownData: PortfolioExposureData = {
      quarter: "",
      date: "",
      exposure: 0,
      weight: 0,
    };

    allQuarters.forEach((quarter) => {
      if (quarterMap.has(quarter)) {
        const existingData = quarterMap.get(quarter);
        if (existingData) lastKnownData = existingData;
      } else {
        quarterMap.set(quarter, {
          quarter,
          date: quarter,
          exposure: lastKnownData.exposure,
          weight: lastKnownData.weight,
        });
      }
    });
  });

  return Array.from(groupedData, ([id, quarterMap]) => ({
    id,
    data: sortQuarters(Array.from(quarterMap.values())),
  }));
};

export const buildFundOptions = (
  inputData: FormattedExposureData[],
): Filter[] => {
  return inputData.map((item) => ({
    key: item.id,
    displayValue: item.id,
  }));
};

export const buildExposureBarsData = (
  dataArray: FormattedExposureData[],
): ExposureBarItem[] => {
  const dates = Array.from(
    new Set(dataArray.flatMap(({ data }) => data.map(({ date }) => date))),
  );

  const nivoData = dates.map((date) => {
    const dataPoint: ExposureBarItem = { date };
    dataArray.forEach(({ id, data }) => {
      const exposure = data.find((d) => d.date === date)?.exposure || 0;
      dataPoint[id] = exposure;
    });
    return dataPoint;
  });

  return nivoData;
};
